import { TypeOfBankTransfer } from "@/apps/tatar/invoiceConfigApp/RACInterfaces";
import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import i18n from "@/i18n";
import { BankAccountDataEU, Contact } from "@/model/db/Contact";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BFMessageBox from "@/modules/abstract-ui/general/Message/BFMessageBox";
import { openEditContact } from "@/modules/contacts-module/ContactSelectMenuItem";
import { FormApi } from "final-form";
import { useRef, useState } from "react";
import { Invoice } from "../../../RAInterfaces";
import { RA_INVOICE_MAIN_FIELDS } from "../../../RAUtils";
// import "./RAInvoiceContactChange.scss";

interface RAInvoiceContactChangeProps {
  prefix?: string;
  invoice: Invoice;
  form: FormApi;
}
const RAInvoiceContactChange = (props: RAInvoiceContactChangeProps) => {
  return (
    <FormFieldValues
      names={[
        `${props.prefix || ""}contact`,
        `${props.prefix || ""}entity`,
        `${props.prefix || ""}paymentType`,
        `${props.prefix || ""}paymentTypeData.type`,
      ]}
    >
      {([contact, entity, paymentType, typeOfBankTransfer]) => (
        <RAInvoiceContactChangeComp
          prefix={props.prefix}
          contactId={contact}
          entity={entity}
          form={props.form}
          invoice={props.invoice}
          paymentType={paymentType}
          typeOfBankTransfer={typeOfBankTransfer}
        />
      )}
    </FormFieldValues>
  );
};

export default RAInvoiceContactChange;

const RAInvoiceContactChangeComp = (props: {
  prefix?: string;
  contactId: string;
  entity: string;
  invoice: Invoice;
  form: FormApi;
  paymentType: string;
  typeOfBankTransfer: TypeOfBankTransfer;
}) => {
  const [contact, setContact] = useState<Contact | null>(null);
  const prevContactId = useRef<string | null>(props.contactId);
  const prevPaymentType = useRef<string | null>(props.paymentType);

  return (
    <FormFieldValues
      names={[
        `${props.prefix || ""}entity`,
        `${props.prefix || ""}paymentType`,
        `${props.prefix || ""}${
          RA_INVOICE_MAIN_FIELDS().invoiceType.identifier
        }`,
        `${props.prefix || ""}paymentTypeData.type`,
        `${props.prefix || ""}paymentTypeData.iban`,
        `${props.prefix || ""}paymentTypeData.bic`,
        `${props.prefix || ""}paymentTypeData.account`,
        `${props.prefix || ""}paymentTypeData.routing`,
        `${props.prefix || ""}paymentTypeData.swift`,
        `${props.prefix || ""}paymentTypeData.nameOfBank`,
        `${props.prefix || ""}paymentTypeData.addressOfBank`,
      ]}
    >
      {([
        entity,
        paymentType,
        invoiceType,
        typeOfBankTransfer,
        iban,
        bic,
        account,
        routing,
        swift,
        nameOfBank,
        addressOfBank,
      ]) => {
        if (!contact) return null;
        if (!entity) return null;

        const overwrites = contact.data.contactOverwrite?.find(
          (e) => e.entity === entity
        );
        if (overwrites) {
          const divergencies = [];

          const selector =
            props.invoice.data.direction === "INCOMING" ? "invoice" : "bill";

          if (overwrites[selector]?.category !== invoiceType) {
            // i18n.t("ra:RAInvoiceContactChange.divergencies.invoiceType", "Kostenkategorie")
            divergencies.push("invoiceType");
          }
          if (overwrites[selector]?.termOfPayment !== paymentType) {
            // i18n.t("ra:RAInvoiceContactChange.divergencies.paymentType", "Zahlungsbedingung")
            divergencies.push("paymentType");
          }
          if (
            typeOfBankTransfer ===
              TypeOfBankTransfer.PAYMENT_IBAN_INTERNATIONAL ||
            typeOfBankTransfer === TypeOfBankTransfer.PAYMENT_IBAN
          ) {
            const availableIbans = contact?.data.bankAccount
              .filter((e) => e.type === "EU")
              .map((e: BankAccountDataEU) => e.iban);
            if (!availableIbans.includes(iban)) {
              // i18n.t("ra:RAInvoiceContactChange.divergencies.iban", "IBAN")
              divergencies.push("iban");
            }
          }

          if (divergencies.length > 0) {
            return (
              <BFMessageBox type="warning" className={`margin-top-20`}>
                <div className={`__flex`}>
                  <div className={`__flex-1`}>
                    <div>
                      {i18n.t(
                        "ra:RAInvoiceContactChange.overwriteIsNotMatching",
                        "Die Stammdaten des Kontakts weichen von den Angaben in der Rechnung ab. Bitte prüfen und ggf. korrigieren:"
                      )}
                    </div>
                    <ul>
                      {divergencies.map((e) => (
                        <li key={e}>
                          {i18n.t(
                            `ra:RAInvoiceContactChange.divergencies.${e}`,
                            e
                          )}
                        </li>
                      ))}
                    </ul>
                    <BFButton
                      className={`margin-top-10`}
                      size={"sm"}
                      onClick={() => {
                        openEditContact(
                          contact,
                          entity,
                          [
                            props.invoice.data.direction === "INCOMING"
                              ? "INVOICE"
                              : "BILL",
                          ],
                          (contact: Contact) => {
                            setContact(contact);
                          }
                        );
                      }}
                      appearance="outline"
                    >
                      {i18n.t(
                        "ra:RAInvoiceContactChange.createOverwrite",
                        "Kontakt bearbeiten"
                      )}
                    </BFButton>
                  </div>
                </div>
              </BFMessageBox>
            );
          } else {
            return null;
          }
        } else {
          return (
            <BFMessageBox type="warning" className={`margin-top-20`}>
              <div className={`__flex`}>
                <div className={`__flex-1`}>
                  {i18n.t(
                    "ra:RAInvoiceContactChange.noOverwriteYet",
                    "Für diesen Kontakt gibt es noch keine gesellschaftsbezogene Überschreibungen. Bitte korrigieren Sie dies."
                  )}
                </div>
                <BFButton
                  onClick={() => {
                    openEditContact(
                      contact,
                      entity,
                      [
                        props.invoice.data.direction === "INCOMING"
                          ? "INVOICE"
                          : "BILL",
                      ],
                      (contact: Contact) => {
                        setContact(contact);
                      }
                    );
                  }}
                  appearance="outline"
                >
                  {i18n.t(
                    "ra:RAInvoiceContactChange.createOverwrite",
                    "Kontakt bearbeiten"
                  )}
                </BFButton>
              </div>
            </BFMessageBox>
          );
        }
      }}
    </FormFieldValues>
  );
};
